import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Download, ArrowRight, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { openInstallPopup } from "@/lib/installStore";

const standalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

// Signed in already: one tap to join (crew logins), or a nudge to sign out (supervisor logins).
export const JoinAccept = ({ code, info, onDone }) => {
  const { user, logout } = useAuth();
  const [busy, setBusy] = useState(false);
  const accept = async () => {
    setBusy(true);
    try { await api.acceptJoin(code); onDone(user.email); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not join this crew"); }
    finally { setBusy(false); }
  };
  if (user.role === "coordinator") {
    return (
      <div className="space-y-4" data-testid="join-supervisor">
        <p className="text-sm">You&apos;re signed in as a supervisor (<span className="font-medium">{user.email}</span>). Sign out first, then create a crew login to join.</p>
        <Button variant="outline" onClick={logout} className="rounded-full" data-testid="join-signout"><LogOut className="w-4 h-4 mr-2" />Sign out</Button>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="join-accept">
      <p className="text-sm">You&apos;re signed in as <span className="font-medium">{user.email}</span>.</p>
      <Button onClick={accept} disabled={busy} className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-white text-base" data-testid="join-accept-btn">
        {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Join the {info.company_name} crew
      </Button>
      <button type="button" onClick={logout} className="w-full text-sm text-muted-foreground hover:text-foreground" data-testid="join-not-me">Not you? Sign out</button>
    </div>
  );
};

export const JoinDone = ({ info, email }) => {
  const navigate = useNavigate();
  return (
    <div className="space-y-5 text-center" data-testid="join-done">
      <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" />
      <div>
        <h2 className="font-display text-2xl font-bold" data-testid="join-done-title">You&apos;re on the crew!</h2>
        <p className="mt-1 text-muted-foreground">Welcome to {info.company_name}{info.depot_name ? ` · ${info.depot_name}` : ""}. Your office can now send you jobs and shifts.</p>
      </div>
      {!standalone() && (
        <div className="rounded-2xl bg-secondary/60 p-4 text-left text-sm space-y-3" data-testid="join-install">
          <p className="font-semibold">Next: put the app on your phone</p>
          <Button onClick={openInstallPopup} className="w-full h-11 rounded-full bg-primary hover:bg-primary/90 text-white" data-testid="join-install-btn"><Download className="w-4 h-4 mr-2" />Install the app</Button>
          <p className="text-muted-foreground">Then open it and sign in with <span className="font-medium text-foreground">{email}</span>.</p>
        </div>
      )}
      <Button variant="outline" onClick={() => navigate("/", { replace: true })} className="w-full h-11 rounded-full" data-testid="join-open-jobs">See my jobs <ArrowRight className="w-4 h-4 ml-2" /></Button>
    </div>
  );
};
